import React, { useEffect, useState } from 'react';
import { Input, Table, DOMHelper, Stack, IconButton, Divider, SelectPicker } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import { AdvanceShareTemplateList as AdvanceShareTemplateListItem, AdvanceShareTemplatePaginationFilterRequest, AdvanceShareTemplateStatusList } from '@/models/advance-share-template.model';
import { getPagedAdvanceShareTemplates } from '@/services/advance-share-template.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getUsersLookup } from '@/services/user.service';
import { jsDateToZone } from '@/utils/date-time';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface AdvanceShareTemplateListProps {
  userId?: number;
}

const AdvanceShareTemplateList: React.FC<AdvanceShareTemplateListProps> = ({ userId }) => {
  const [filterReq, setFilterReq] = useState<AdvanceShareTemplatePaginationFilterRequest>(() => {
    const req = new PaginationFilterRequest() as AdvanceShareTemplatePaginationFilterRequest;
    req.userId = userId;
    return req;
  });
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<AdvanceShareTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AdvanceShareTemplatePaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = { ...req, userId: userId ?? req.userId };
      const result: PagedResponseModel<AdvanceShareTemplateListItem> = await getPagedAdvanceShareTemplates(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching advance share templates:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          {!userId && <FetchingSelect 
            placeholder= "Select a User"
            fetchData={(k, p) => getUsersLookup(k, undefined, p)}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, userId: dataKey ?? undefined }))}
            value={filterReq.userId ?? null}
            reFetchOnOpen
          />}
          <SelectPicker
            label="Status"
            data={AdvanceShareTemplateStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, status: dataKey ?? undefined }))}
          />
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>User</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareTemplateListItem) => `${rowData.user?.firstName} ${rowData.user?.lastName}`}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Version</HeaderCell>
          <Cell dataKey="version" />
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Status</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareTemplateListItem) => (AdvanceShareTemplateStatusList.findLast(s => s.value == rowData.status)?.label)}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Bookmarks</HeaderCell>
          <Cell dataKey="bookmarkCount" />
        </Column>
        <Column flexGrow={0.7} align="right" sortable>
          <HeaderCell>Price</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareTemplateListItem) =>
              rowData.price ? `${rowData.price} pts` : 'Free'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Created At</HeaderCell>
          <Cell>
            {(rowData: AdvanceShareTemplateListItem) => jsDateToZone(rowData.createdAt) }
          </Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: AdvanceShareTemplateListItem) => (
              <IconButton onClick={() => nav(`/advance-shares/templates/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

    </PageContent>
  );
};

export default AdvanceShareTemplateList;
